import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { BackendApiBrowser } from "./backend-api-browser";
import { fetchBackendApiCoverageSnapshot } from "./backend-api-coverage-fetch";
import { backendApiCoverageStyles as styles } from "./backend-api-coverage-styles";
import { BackendApiCoverageSummaryCards } from "./backend-api-coverage-summary";
import type { BackendApiCoverageSnapshot } from "./backend-api-coverage-types";

export function BackendApiCoveragePage(): React.JSX.Element {
  const { slug } = useParams();
  const [snapshot, setSnapshot] = useState<BackendApiCoverageSnapshot | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setSnapshot(await fetchBackendApiCoverageSnapshot());
    } catch (err) {
      setSnapshot(null);
      setError(
        err instanceof Error ? err.message : t("api_coverage.snapshot_failed"),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title={t("api_coverage.title")}
        description={t("api_coverage.description")}
        actions={
          <>
            <Link to="/system/backend-api-tests" style={styles.pagerButton}>
              {t("api_coverage.open_test_catalog")}
            </Link>
            <button type="button" onClick={load} style={shared.refreshBtn}>
              {t("common.refresh")}
            </button>
          </>
        }
      />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && <p style={shared.loadingText}>{t("common.loading")}</p>}
      {snapshot ? (
        <>
          <p style={styles.auditMeta}>
            {t("api_coverage.generated_at")}:{" "}
            {new Date(snapshot.generated_at).toLocaleString()}
          </p>
          <BackendApiCoverageSummaryCards snapshot={snapshot} />
          <BackendApiBrowser snapshot={snapshot} selectedSlug={slug} />
        </>
      ) : null}
    </div>
  );
}
